import * as moment from 'moment';
import { roundToNearestMinutes } from '@ordersnproducts/utils/utils';
import {
  IRMXSameDayAfterCutoffConfig,
  IRMXSameDayOrderConfig,
} from '@ordersnproducts/modules/shared/services/ce-order.services';
import { CalendarSlot } from '@ordersnproducts/modules/multiple-order-online/redux/actions/shipment-location-worksheets.actions';

// Use this function to emulate and test current date
export function getCurrentDay(tzCode?: string): moment.Moment {
  return tzCode ? moment(new Date()).tz(tzCode) : moment(new Date());
  // return moment('Sun Feb 02 2025 13:36:23 GMT+0100 (Central European Standard Time)'); // .tz(tzCode);
}

/**
 * Get the minimum time for the same day order.
 * @returns The minimum time for the same day order.
 * @param timeZone
 */
export function getMinTimeSameDayOrderByDCOConfig(timeZone: string, rmxConfig: IRMXSameDayAfterCutoffConfig): Date {
  if (!rmxConfig && rmxConfig.RMX_SDAC_INITIAL_BUSINESS_HOUR) {
    return undefined;
  }

  const currentDate = roundToNearestMinutes(getCurrentDay(timeZone), 5).tz(timeZone);
  const [blackoutHours, blackoutMinutes] = rmxConfig.RMX_SDAC_BLACKOUT_PERIOD.split(':').map(Number);
  const initialBusinessHour = moment()
    .tz(timeZone)
    .set({
      hours: moment(rmxConfig.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').hours(),
      minutes: moment(rmxConfig.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').minutes(),
      seconds: moment(rmxConfig.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').seconds(),
    });

  if (currentDate.isSameOrBefore(initialBusinessHour)) {
    return getAdjustedDate(currentDate, rmxConfig.RMX_SDAC_INITIAL_BUSINESS_HOUR, blackoutHours, blackoutMinutes);
  }

  if (isCurrentDateAfterOrderingHoursByConfig(rmxConfig, 5, false, timeZone)) {
    return currentDate.startOf('day').toDate();
  }

  return getAdjustedDate(currentDate, null, blackoutHours, blackoutMinutes);
}

/**
 * Get the minimum time for the same day order.
 * @returns The minimum time for the same day order.
 * @param timeZone
 */
export function getMinTimeSameDayOrder(timeZone: string, rmxUKConfigInfo: IRMXSameDayOrderConfig): Date {
  if (!rmxUKConfigInfo) {
    return moment().toDate();
  }

  const currentDate = roundToNearestMinutes(getCurrentDay(timeZone), 10).tz(timeZone);
  const rmxUKInfo: IRMXSameDayAfterCutoffConfig = getUKInfoConfig(currentDate, rmxUKConfigInfo);
  const [blackoutHours, blackoutMinutes] = rmxUKInfo.RMX_SDAC_BLACKOUT_PERIOD.split(':').map(Number);
  const initialBusinessHour = moment()
    .tz(timeZone)
    .set({
      hours: moment(rmxUKInfo.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').hours(),
      minutes: moment(rmxUKInfo.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').minutes(),
      seconds: moment(rmxUKInfo.RMX_SDAC_INITIAL_BUSINESS_HOUR, 'HH:mm').seconds(),
    });

  if (currentDate.isSameOrBefore(initialBusinessHour)) {
    return getAdjustedDate(currentDate, rmxUKInfo.RMX_SDAC_INITIAL_BUSINESS_HOUR, blackoutHours, blackoutMinutes);
  }

  if (isCurrentDateAfterOrderingHours(rmxUKConfigInfo, timeZone)) {
    return currentDate.startOf('day').toDate();
  }

  return getAdjustedDate(currentDate, null, blackoutHours, blackoutMinutes);
}

/**
 * Adjusts the given date by setting it to the initial business hour (if provided)
 * and then adding the blackout hours and minutes.
 *
 * @param currentDate - The current date to be adjusted.
 * @param initialBusinessHour - The initial business hour in 'HH:mm' format, or null.
 * @param blackoutHours - The number of blackout hours to add.
 * @param blackoutMinutes - The number of blackout minutes to add.
 * @returns The adjusted date.
 */
function getAdjustedDate(
  currentDate: moment.Moment,
  initialBusinessHour: string | null,
  blackoutHours: number,
  blackoutMinutes: number,
): Date {
  if (initialBusinessHour) {
    const [initialHours, initialMinutes] = initialBusinessHour.split(':').map(Number);
    currentDate.set({ hours: initialHours, minutes: initialMinutes });
  }

  currentDate.add(blackoutHours, 'hours').add(blackoutMinutes, 'minutes');

  return moment()
    .set({
      hours: currentDate.hours(),
      minutes: currentDate.minutes(),
      seconds: 0,
      milliseconds: 0,
    })
    .toDate();
}

/**
 * Checks if the current date is after the cutoff time.
 *
 * @param timeZone - The time zone to use for the current date.
 * @returns True if the current date is after the cutoff time, false otherwise.
 */
export function isCurrentDateAfterOrderingHours(rmxUKConfigInfo: IRMXSameDayOrderConfig, timeZone?: string): boolean {
  if (!rmxUKConfigInfo) {
    return false;
  }

  const currentDate = roundToNearestMinutes(getCurrentDay(timeZone), 10).tz(timeZone);
  const rmxUKInfo: IRMXSameDayAfterCutoffConfig = getUKInfoConfig(currentDate, rmxUKConfigInfo);
  return isCurrentDateAfterOrderingHoursByConfig(rmxUKInfo, 10, true, timeZone);
}

/**
 * Checks if the current date is after the ordering hours as defined in the given configuration.
 *
 * This function validates whether the current time, adjusted for the blackout period, has surpassed
 * the final allowed ordering time for the day based on the configuration.
 *
 * @param rmxInfo - The configuration object containing the blackout period and final business hours.
 * @param roundTo - The number of minutes to round the current time to.
 * @param subBlackout - A boolean indicating whether to subtract the blackout period from the final business hours.
 * @param timeZone - An optional time zone to calculate the current time. If not specified, the default system time zone is used.
 * @returns True if the current date and time exceed the final allowable ordering time, false otherwise.
 */
export function isCurrentDateAfterOrderingHoursByConfig(
  rmxInfo: IRMXSameDayAfterCutoffConfig,
  roundTo: number,
  subBlackout: boolean,
  timeZone?: string,
): boolean {
  if (!(rmxInfo && rmxInfo.RMX_SDAC_FINAL_BUSINESS_HOUR)) {
    return false;
  }

  const currentDate = roundToNearestMinutes(getCurrentDay(timeZone), roundTo).tz(timeZone);
  const [blackoutHours, blackoutMinutes] = rmxInfo.RMX_SDAC_BLACKOUT_PERIOD?.split(':')?.map(Number) ?? [];
  const [finalBusinessHours, finalBusinessMinutes] = rmxInfo.RMX_SDAC_FINAL_BUSINESS_HOUR?.split(':')?.map(Number) ?? [];

  const finalOrderingHours = currentDate.clone().set({
    hours: finalBusinessHours,
    minutes: finalBusinessMinutes,
  });
  if (rmxInfo.RMX_SDAC_FINAL_DELIVERY_HOURS) {
    const [finalDeliveryHours, finalDeliverMinutes] = rmxInfo.RMX_SDAC_FINAL_DELIVERY_HOURS.split(':').map(Number);

    const maxDeliveryTime = currentDate.clone().set({
      hours: finalDeliveryHours,
      minutes: finalDeliverMinutes,
    });

    const minDeliveryTime = getCurrentDay(timeZone).add(blackoutHours, 'hours').add(blackoutMinutes, 'minutes');

    if (minDeliveryTime.isSameOrAfter(maxDeliveryTime)) {
      return true;
    }
  }


  // Smth wrong with this cutoffTime
  let endOrderingHours = finalOrderingHours;

  if (subBlackout) {
    endOrderingHours = finalOrderingHours.clone().subtract(blackoutHours, 'hours').subtract(blackoutMinutes, 'minutes');
  }

  return currentDate.isAfter(endOrderingHours);
}

/**
 * Retrieves the UK Same day order configuration information based on the current date.
 *
 * @param currentDate - The current date.
 * @param rmxUKConfigInfo - The configuration information for the same day order.
 * @returns The UK configuration information for the same day order.
 */
export function getUKInfoConfig(
  currentDate: moment.Moment,
  rmxUKConfigInfo: IRMXSameDayOrderConfig,
): IRMXSameDayAfterCutoffConfig {
  // If the current day is a Saturday, return the Saturday configuration
  if (
    rmxUKConfigInfo['OT_SAME_DAY_AFTER_CUTOFF_DAYS_LIST']['RMX_SDAC_SHORT_DAY_LIST'].some(
      (day) => day.NAMEOFDAY === currentDate.format('dddd'),
    ) ||
    currentDate.day() === 0
  ) {
    return rmxUKConfigInfo['OT_SAME_DAY_AFTER_CUTOFF_TIME_SHORT'] as IRMXSameDayAfterCutoffConfig;
  }

  return rmxUKConfigInfo['OT_SAME_DAY_AFTER_CUTOFF_TIME_REGULAR'] as IRMXSameDayAfterCutoffConfig;
}

/**
 * Maps a `BusinessDay` object to an `IRMXSameDayAfterCutoffConfig` object.
 *
 * @param data - The `BusinessDay` object containing the initial time, final time, and reaction time.
 * @returns The mapped `IRMXSameDayAfterCutoffConfig` object.
 */
export function mapCalendarItemToSameDayConfig(data: CalendarSlot): IRMXSameDayAfterCutoffConfig {
  return {
    RMX_SDAC_INITIAL_BUSINESS_HOUR: data.initialTime,
    RMX_SDAC_FINAL_BUSINESS_HOUR: data.finalTime,
    RMX_SDAC_BLACKOUT_PERIOD: data.reactionTime ? `${data.reactionTime}:00` : undefined,
  } as IRMXSameDayAfterCutoffConfig;
}
